import { Ionicons } from "@expo/vector-icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet, Text, View } from "react-native";

interface PasswordValidatorProps {
  password: string;
}

export default function PasswordValidator({ password }: PasswordValidatorProps) {
  const { t } = useTranslation();
  const hasMinLength = password.length >= 8; // Changed from 6 to 8 to match validation
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasDigit = /[0-9]/.test(password);

  const isValid = hasMinLength && hasUppercase && hasLowercase && hasDigit;

  // Don't show anything if password is empty
  if (!password) return null;

  return (
    <View style={styles.container}>
      <ValidationItem
        isValid={hasMinLength}
        text={t("createAccount.passwordValidator.minLength")}
      />
      <ValidationItem
        isValid={hasUppercase}
        text={t("createAccount.passwordValidator.uppercase")}
      />
      <ValidationItem
        isValid={hasLowercase}
        text={t("createAccount.passwordValidator.lowercase")}
      />
      <ValidationItem
        isValid={hasDigit}
        text={t("createAccount.passwordValidator.digit")}
      />
      {isValid && (
        <View style={styles.successMessage}>
          <Ionicons name="shield-checkmark" size={16} color="#1C453F" />
          <Text style={styles.successText}>{t("createAccount.passwordValidator.secure")}</Text>
        </View>
      )}
    </View>
  );
}

interface ValidationItemProps {
  isValid: boolean;
  text: string;
}

function ValidationItem({ isValid, text }: ValidationItemProps) {
  return (
    <View style={styles.item}>
      <Ionicons 
        name={isValid ? "checkmark-circle" : "ellipse-outline"} 
        size={16} 
        color={isValid ? "#1C453F" : "#999"} 
      />
      <Text style={[styles.itemText, isValid && styles.itemTextValid]}>
        {text}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    paddingHorizontal: 4,
  },
  item: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 4,
  },
  itemText: {
    fontFamily: "Inter_400Regular",
    fontSize: 13,
    color: "#999",
  },
  itemTextValid: {
    color: "#1C453F",
  },
  successMessage: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: "rgba(28, 69, 63, 0.1)",
    borderRadius: 8,
  },
  successText: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 13,
    color: "#1C453F",
  },
});
